/**
 * ProviderHub — patrón oficial para configurar proveedores externos de Structura.
 *
 * Uso declarativo:
 *   <ProviderHub providers={["tusfacturas", "arca_directo"]} />
 *   <ProviderHub providers={["shipnow", "treggo", "oca"]} groupByCategory />
 *
 * Cada provider resuelve automáticamente nombre, ícono, descripción, categoría,
 * feature flag, plan mínimo, estado y componente de configuración desde
 * PROVIDER_REGISTRY en src/lib/provider-registry.
 */
import { Suspense, useEffect, useState, type ReactNode } from "react";
import { ExternalLink, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenantContext } from "@/lib/tenant-context";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { PageLoader } from "@/components/PageLoader";
import {
  PROVIDER_REGISTRY,
  CATEGORY_LABELS,
  resolveProviderStatus,
  type ProviderCategory,
  type ProviderDescriptor,
  type ProviderStatus,
} from "@/lib/provider-registry";
import { paymentProviderLogo, logisticsProviderLogo } from "@/lib/provider-logos";
import { apiGetJson } from "@/lib/sm-api";
import {
  invoicingStatusApiPath,
  resolveInvoicingConnectionStatus,
  type InvoicingConfigSnap,
} from "@/lib/invoicing-provider-status";

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  ProviderStatus,
  { label: string; classes: string }
> = {
  configured:           { label: "Conectado",            classes: "bg-success/15 text-success" },
  not_configured:       { label: "Sin conectar",         classes: "bg-muted text-muted-foreground" },
  requires_credentials: { label: "Falta configurar",     classes: "bg-warning/15 text-warning" },
  requires_plan:        { label: "Otro plan",            classes: "bg-primary/15 text-primary" },
  sandbox:              { label: "Prueba",               classes: "bg-info/15 text-info" },
  production:           { label: "Conectado",            classes: "bg-success/15 text-success" },
  disabled:             { label: "Pausado",              classes: "bg-muted text-muted-foreground" },
  error:                { label: "Con error",            classes: "bg-danger/15 text-danger" },
  unavailable:          { label: "No disponible",        classes: "bg-muted text-muted-foreground" },
  deprecated:           { label: "No disponible",        classes: "bg-muted text-muted-foreground" },
  beta:                 { label: "Beta",                 classes: "bg-info/15 text-info" },
  coming_soon:          { label: "Próximamente",         classes: "bg-muted text-text-disabled" },
};

interface ProviderStatusBadgeProps {
  status: ProviderStatus;
  className?: string;
}

export function ProviderStatusBadge({ status, className }: ProviderStatusBadgeProps) {
  const { label, classes } = STATUS_CONFIG[status] ?? STATUS_CONFIG.not_configured;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium leading-none",
        classes,
        className,
      )}
    >
      {label}
    </span>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

interface ProviderCardProps {
  descriptor: ProviderDescriptor;
  status: ProviderStatus;
  onSelect: () => void;
}

export function ProviderCard({ descriptor, status, onSelect }: ProviderCardProps) {
  const Icon = descriptor.icon;
  const logo = paymentProviderLogo(descriptor.id) ?? logisticsProviderLogo(descriptor.id);
  const isActive = status === "configured" || status === "production" || status === "sandbox";
  const isUnavailable = status === "coming_soon" || status === "unavailable" || status === "deprecated";
  const isPlan = status === "requires_plan";

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isUnavailable}
      className={cn(
        "group relative flex w-full flex-col gap-2 rounded-lg border p-3 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isActive
          ? "border-success/30 bg-success/5 hover:border-success/50"
          : isUnavailable
            ? "cursor-not-allowed border-border/50 bg-muted opacity-60"
            : isPlan
              ? "border-primary/20 bg-primary/5 hover:border-primary/40"
              : "border-border bg-card hover:border-primary/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {logo ? (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white p-1">
              <img src={logo} alt="" className="h-full w-full object-contain" />
            </span>
          ) : Icon ? (
            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                isActive ? "bg-emerald-500/20 text-emerald-500" : "bg-primary/10 text-primary",
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
          ) : null}
          <div className="min-w-0">
            <span className="block truncate text-[13px] font-semibold text-foreground">
              {descriptor.name}
            </span>
          </div>
        </div>
        {descriptor.docsUrl && !isUnavailable && (
          <a
            href={descriptor.docsUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
            title="Ver documentación"
            aria-label={`Documentación de ${descriptor.name}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <p className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">
        {descriptor.description}
      </p>

      <div className="mt-auto flex items-center justify-between gap-2">
        <ProviderStatusBadge status={status} />
        {!isUnavailable && (
          <span
            className={cn(
              "text-[11px] font-medium",
              isActive ? "text-emerald-500" : "text-primary",
            )}
          >
            {isActive ? "Configurar →" : isPlan ? "Ver planes →" : "Configurar →"}
          </span>
        )}
      </div>
    </button>
  );
}

// ─── Back ─────────────────────────────────────────────────────────────────────

interface ProviderBackProps {
  title: string;
  description?: string;
  onBack: () => void;
  backLabel?: string;
}

export function ProviderBack({ title, description, onBack, backLabel = "Configuración" }: ProviderBackProps) {
  return (
    <div className="mb-5 space-y-1.5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        aria-label={`Volver a ${backLabel}`}
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {backLabel}
      </button>
      <div>
        <h2 className="text-lg font-semibold leading-tight text-foreground">{title}</h2>
        {description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

export function ProviderEmptyState({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
      <p className="text-sm text-muted-foreground">
        {message ?? "No hay proveedores disponibles en esta categoría."}
      </p>
    </div>
  );
}

// ─── Help ─────────────────────────────────────────────────────────────────────

export function ProviderHelp({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300">
      {children}
    </div>
  );
}

// ─── Hub (entry point) ────────────────────────────────────────────────────────

interface ProviderHubProps {
  /** IDs from PROVIDER_REGISTRY */
  providers: string[];
  /** Group cards by category with labeled sections */
  groupByCategory?: boolean;
  /** Intro text shown above the cards grid */
  intro?: string;
  /** Custom help block below the grid */
  help?: ReactNode;
  /** Label for the detail back link */
  backLabel?: string;
}

export function ProviderHub({
  providers,
  groupByCategory = false,
  intro,
  help,
  backLabel = "Configuración",
}: ProviderHubProps) {
  const { productFlags } = useTenantContext();
  const [selected, setSelected] = useState<string | null>(null);

  const descriptors = providers
    .map((id) => PROVIDER_REGISTRY[id])
    .filter(Boolean);

  // ── Detail view ──────────────────────────────────────────────────────────────
  if (selected !== null) {
    const descriptor = PROVIDER_REGISTRY[selected];
    if (!descriptor) {
      setSelected(null);
      return null;
    }
    const ConfigPanel = descriptor.ConfigPanel;
    return (
      <div>
        <ProviderBack
          title={descriptor.name}
          description={descriptor.description}
          onBack={() => setSelected(null)}
          backLabel={backLabel}
        />
        {ConfigPanel ? (
          <Suspense fallback={<PageLoader />}>
            <ConfigPanel />
          </Suspense>
        ) : (
          <div className="rounded-lg border border-border/60 bg-muted/20 p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Panel de configuración próximamente disponible.
            </p>
          </div>
        )}
      </div>
    );
  }

  // ── Grid view ────────────────────────────────────────────────────────────────
  if (groupByCategory) {
    const categories = [
      ...new Set(descriptors.map((d) => d.category)),
    ] as ProviderCategory[];

    return (
      <div className="space-y-6">
        {intro && <p className="text-sm text-muted-foreground">{intro}</p>}
        {categories.map((cat) => {
          const catDescriptors = descriptors.filter((d) => d.category === cat);
          return (
            <div key={cat} className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {CATEGORY_LABELS[cat]}
              </h3>
              <ProviderCardGrid
                descriptors={catDescriptors}
                productFlags={productFlags}
                onSelect={setSelected}
              />
            </div>
          );
        })}
        {help && <div>{help}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {intro && <p className="text-sm text-muted-foreground">{intro}</p>}
      {descriptors.length === 0 ? (
        <ProviderEmptyState />
      ) : (
        <ProviderCardGrid
          descriptors={descriptors}
          productFlags={productFlags}
          onSelect={setSelected}
        />
      )}
      {help && <div>{help}</div>}
    </div>
  );
}

// ─── Internal card grid ───────────────────────────────────────────────────────

function ProviderCardGrid({
  descriptors,
  productFlags,
  onSelect,
}: {
  descriptors: ProviderDescriptor[];
  productFlags: Record<string, boolean | undefined> | undefined;
  onSelect: (id: string) => void;
}) {
  const tenantId = useWorkspaceTenantId();
  const [invoicingStatus, setInvoicingStatus] = useState<Record<string, ProviderStatus>>({});

  useEffect(() => {
    const invoicing = descriptors.filter((d) => d.category === "invoicing");
    if (!tenantId || invoicing.length === 0) {
      setInvoicingStatus({});
      return;
    }

    let cancelled = false;
    void (async () => {
      const next: Record<string, ProviderStatus> = {};
      await Promise.all(
        invoicing.map(async (descriptor) => {
          const path = invoicingStatusApiPath(descriptor.id);
          const flagEnabled = Boolean(descriptor.flagKey && productFlags?.[descriptor.flagKey]);
          if (!path) {
            next[descriptor.id] = resolveProviderStatus(
              descriptor,
              productFlags as Record<string, boolean> | undefined,
            );
            return;
          }
          let config: InvoicingConfigSnap | null = null;
          try {
            const r = await apiGetJson<{ config: InvoicingConfigSnap | null }>(path, tenantId);
            config = r.config
              ? {
                  enabled: r.config.enabled,
                  configured: r.config.configured,
                  environment: r.config.environment ?? null,
                }
              : null;
          } catch {
            config = null;
          }
          next[descriptor.id] = resolveInvoicingConnectionStatus({
            flagEnabled,
            minPlan: descriptor.minPlan,
            config,
          });
        }),
      );
      if (!cancelled) setInvoicingStatus(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [descriptors, productFlags, tenantId]);

  return (
    <div
      className={cn(
        "grid gap-3",
        descriptors.length === 1
          ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          : descriptors.length === 2
            ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
      )}
    >
      {descriptors.map((descriptor) => {
        const base = resolveProviderStatus(
          descriptor,
          productFlags as Record<string, boolean> | undefined,
        );
        const status =
          descriptor.category === "invoicing"
            ? (invoicingStatus[descriptor.id] ??
              // Mientras carga: no mostrar "Conectado" solo por flag del plan
              (base === "configured" ? "requires_credentials" : base))
            : base;
        return (
          <ProviderCard
            key={descriptor.id}
            descriptor={descriptor}
            status={status}
            onSelect={() => onSelect(descriptor.id)}
          />
        );
      })}
    </div>
  );
}
